'use client';

// Переиспользуемый композер ввода — ТОТ ЖЕ дизайн, что у генератора на лендинге:
// скруглённая карточка, круглые кнопки внутри (микрофон + отправка), эквалайзер и
// спиннер при распознавании речи. Использует generator.module.css (1:1 внешний вид).
// Контролируемый: value/onChange/onSubmit снаружи; запись голоса — внутри (useVoiceInput).
import { useEffect, useRef, useState } from 'react';
import styles from './generator.module.css';
import { MicIcon, SendIcon } from './icons';
import { useVoiceInput } from './use-voice-input';

export interface ComposerDict {
  placeholder: string;
  micTranscribing: string;
  micStart: string;
  micStop: string;
  micError: string;
  /** Отмена записи голосового (нужен только с onVoice). */
  micCancel?: string;
}

interface ComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  dict: ComposerDict;
  /** URL распознавания речи (тот же, что у генератора). Пусто → кнопки микрофона нет. */
  transcribeUrl?: string;
  /** Внешняя занятость (идёт ответ ассистента) — блокирует отправку. */
  busy?: boolean;
  /** Компактный вид (меньше паддинги/скругление) — для кабинета/узких мест. */
  compact?: boolean;
  /** Двухэтажный вид (чат кабинета, канон пробы /chat-probe.html):
      поле во всю ширину сверху, кнопки строкой ниже справа. */
  stack?: boolean;
  /** Кнопка перед микрофоном (скрепка в переписке с человеком). Пусто — слота нет. */
  left?: React.ReactNode;
  /** Голосовое сообщение (переписка с человеком, М7): запись уходит наружу блобом,
   *  распознавания НЕТ. Задан — микрофон работает даже без transcribeUrl. */
  onVoice?: (blob: Blob, seconds: number) => void;
  /** Потолок записи в секундах: дошли — останавливаемся сами (голосовое на час никому
   *  не нужно, да и предел размера у каналов свой). */
  maxVoiceSec?: number;
}

export function Composer({
  value,
  onChange,
  onSubmit,
  dict,
  transcribeUrl,
  busy = false,
  compact = false,
  stack = false,
  left,
  onVoice,
  maxVoiceSec = 300,
}: ComposerProps) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  });
  const [micErr, setMicErr] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const { recording, transcribing, vizRef, toggle, cancel } = useVoiceInput({
    transcribeUrl,
    onBlob: onVoice,
    onText: (text) => {
      const prev = valueRef.current;
      onChange((prev ? prev + ' ' : '') + text);
      requestAnimationFrame(resize);
      taRef.current?.focus();
    },
    onError: () => setMicErr(true),
  });

  function resize() {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  }

  // Высоту textarea пересчитываем после любого изменения текста (в т.ч. после
  // распознавания речи, когда textarea заново монтируется).
  useEffect(() => {
    if (!recording) resize();
  }, [value, recording, transcribing]);

  // Счётчик времени записи голосового: человек должен видеть, сколько наговорил, и
  // не упереться в предел молча — на потолке останавливаем запись сами (она уйдёт).
  useEffect(() => {
    if (!recording || !onVoice) {
      setElapsed(0);
      return;
    }
    const started = Date.now();
    const timer = setInterval(() => {
      const sec = Math.floor((Date.now() - started) / 1000);
      setElapsed(sec);
      if (sec >= maxVoiceSec) toggle();
    }, 500);
    return () => clearInterval(timer);
    // toggle пересоздаётся каждый рендер — в зависимости не берём, иначе таймер
    // перезапускался бы и счётчик стоял на нуле.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording, onVoice, maxVoiceSec]);

  const mmss = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`;
  const canSend = !!value.trim() && !recording && !transcribing && !busy;

  function submit() {
    if (!canSend) return;
    onSubmit();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  return (
    <div
      className={`${styles.composer} ${compact ? styles.composerCompact : ''} ${stack ? styles.composerStack : ''}`}
    >
      <div
        className={`${styles.prompt} ${compact ? styles.promptCompact : ''} ${stack ? styles.promptStack : ''} ${recording ? styles.promptRec : ''}`}
      >
        {recording ? (
          <div ref={vizRef} className={styles.viz} aria-hidden>
            {Array.from({ length: 48 }).map((_, i) => (
              <span key={i} />
            ))}
          </div>
        ) : (
          <textarea
            ref={taRef}
            rows={1}
            value={value}
            // Без автокоррекции/подсказок клавиатуры: в чате они мешают (iOS-панель
            // предиктивного набора скрывается только при выключенных autoCorrect+spellCheck).
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            disabled={transcribing}
            placeholder={transcribing ? dict.micTranscribing : dict.placeholder}
            onChange={(e) => {
              onChange(e.target.value);
              resize();
            }}
            onKeyDown={onKeyDown}
          />
        )}
        {recording && onVoice && <span className={styles.recTime}>{mmss}</span>}
        {left}
        {recording && onVoice && (
          <button
            type="button"
            className={styles.iconBtn}
            onClick={cancel}
            title={dict.micCancel}
            aria-label={dict.micCancel}
          >
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden>
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        )}
        {(transcribeUrl || onVoice) && (
          <button
            type="button"
            className={`${styles.iconBtn} ${recording ? styles.rec : ''}`}
            onClick={() => {
              setMicErr(false);
              toggle();
            }}
            disabled={transcribing || busy}
            title={recording ? dict.micStop : dict.micStart}
            aria-label={recording ? dict.micStop : dict.micStart}
          >
            {transcribing ? (
              <span className={styles.spin} aria-hidden />
            ) : recording ? (
              <span className={styles.stopIcon} aria-hidden />
            ) : (
              <MicIcon />
            )}
          </button>
        )}
        <button
          type="button"
          className={`${styles.iconBtn} ${styles.send} ${recording ? styles.hide : ''}`}
          // Кнопка не отбирает фокус у поля: клавиатура не мигает при отправке
          // (канон клавиатуры чата, эталон /chat-probe.html).
          onPointerDown={(e) => e.preventDefault()}
          onClick={submit}
          disabled={!canSend}
          aria-label="Send"
        >
          <SendIcon />
        </button>
      </div>
      {micErr && <div className={styles.micErr}>{dict.micError}</div>}
    </div>
  );
}
